import {
  Alert,
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type React from 'react';
import { useState } from 'react';
import { inviteUser } from '../../services/api';
import type { UserRole } from '../../types/auth';
import { BaseModal } from '../common/UI';

interface InviteUserDialogProps {
  open: boolean;
  onClose: () => void;
  onUserInvited: () => void;
  companyId?: string;
}

interface FormData {
  email: string;
  name: string;
  role: UserRole;
}

const InviteUserDialog: React.FC<InviteUserDialogProps> = ({
  open,
  onClose,
  onUserInvited,
  companyId,
}) => {
  const [formData, setFormData] = useState<FormData>({
    email: '',
    name: '',
    role: 'user',
  });
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const theme = useTheme();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRoleChange = (event: SelectChangeEvent<string>) => {
    setFormData((prev) => ({ ...prev, role: event.target.value as UserRole }));
  };

  const handleSave = async (): Promise<void> => {
    if (formData.name.length > 50) {
      setError('Name cannot exceed 50 characters.');
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      const payload: Parameters<typeof inviteUser>[0] = {
        email: formData.email,
        role: formData.role,
        displayName: formData.name,
      };
      if (companyId) {
        payload.companyId = companyId;
      }
      await inviteUser(payload);
      onUserInvited();
      onClose();
    } catch (err: unknown) {
      const error = err as Error;
      setError(error.message || 'Failed to invite user.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = (): void => {
    setFormData({ email: '', name: '', role: 'user' });
    setError(null);
    onClose();
  };

  return (
    <BaseModal
      open={open}
      onClose={handleClose}
      title="Invite New User"
      onConfirm={handleSave}
      confirmText="Send Invitation"
      confirmLoading={isSaving}
    >
      <Alert
        severity="info"
        sx={{
          mb: 2,
          backgroundColor: alpha(theme.palette.bidlogiq.orange, 0.25),
          color: 'text.primary',
          '& .MuiAlert-icon': {
            color: 'text.primary',
          },
        }}
      >
        An invitation will be sent to the user's email. You can only invite users who share your
        organization's email domain.
      </Alert>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <TextField
        autoFocus
        margin="dense"
        name="name"
        label="Full Name"
        type="text"
        fullWidth
        variant="outlined"
        value={formData.name}
        onChange={handleChange}
        inputProps={{ maxLength: 50 }}
        helperText={`${formData.name.length}/50`}
        error={formData.name.length > 50}
      />
      <FormControl fullWidth margin="dense">
        <InputLabel id="role-select-label">Role</InputLabel>
        <Select
          labelId="role-select-label"
          value={formData.role}
          label="Role"
          onChange={handleRoleChange}
          renderValue={(selected) => {
            switch (selected) {
              case 'client_admin':
                return 'Client Admin';
              case 'team_lead':
                return 'Team Lead';
              case 'user':
                return 'User';
              case 'restricted_editor':
                return 'Restricted Editor';
              case 'evaluator':
                return 'Procurement Evaluator';
              default:
                return selected;
            }
          }}
        >
          <MenuItem value="client_admin">
            <Box>
              <Typography variant="body1">Client Admin</Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ whiteSpace: 'normal', mt: 0.5 }}
              >
                Full access to client settings, billing, templates, user/team management, and all
                tenders across the organization.
              </Typography>
            </Box>
          </MenuItem>
          <MenuItem value="team_lead">
            <Box>
              <Typography variant="body1">Team Lead</Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ whiteSpace: 'normal', mt: 0.5 }}
              >
                Can manage team members, upload team templates/library files, and view all tenders
                assigned to their team.
              </Typography>
            </Box>
          </MenuItem>
          <MenuItem value="user">
            <Box>
              <Typography variant="body1">User</Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ whiteSpace: 'normal', mt: 0.5 }}
              >
                Standard access. Can view the library, create personal tenders, and edit tenders
                they are explicitly assigned to.
              </Typography>
            </Box>
          </MenuItem>
          <MenuItem value="restricted_editor">
            <Box>
              <Typography variant="body1">Restricted Editor</Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ whiteSpace: 'normal', mt: 0.5 }}
              >
                Limited access. Can only view and edit specific tender sections explicitly assigned
                to them.
              </Typography>
            </Box>
          </MenuItem>
          <MenuItem value="evaluator">
            <Box>
              <Typography variant="body1">Procurement Evaluator</Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ whiteSpace: 'normal', mt: 0.5 }}
              >
                Procurement workspace access only. Can view and score supplier submissions they are
                assigned to evaluate.
              </Typography>
            </Box>
          </MenuItem>
        </Select>
        <FormHelperText>
          Select a base role. You can customize their specific permissions in the User Management
          panel after inviting them.
        </FormHelperText>
      </FormControl>
      <TextField
        margin="dense"
        name="email"
        label="Email Address"
        type="email"
        fullWidth
        variant="outlined"
        value={formData.email}
        onChange={handleChange}
      />
    </BaseModal>
  );
};

export default InviteUserDialog;
